import React, { useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { UseQueryResult, useQuery } from 'react-query'
import { Role, Action } from '../../types'
import { fetchRoles } from '../../api/API'
import StyledChip from '../../components/StyledChip'
import { Alert, AlertTitle, Box, Button, Paper, Typography, Divider, Collapse, IconButton } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import SearchIcon from '@mui/icons-material/Search'
import CreateIcon from '@mui/icons-material/Create'
import DoneAllIcon from '@mui/icons-material/DoneAll'

const actionToIcon = {
  [Action.READ]: <SearchIcon />,
  [Action.WRITE]: <CreateIcon />,
  [Action.READ_WRITE]: <DoneAllIcon />,
}

const RoleDetails: React.FC = () => {
  const history = useHistory()
  const { roleId } = useParams<{ roleId: string }>()

  const [appPermExpanded, setAppPermExpanded] = useState(true)
  const [dataPermExpanded, setDataPermExpanded] = useState(true)
  const [globalPermExpanded, setGlobalPermExpanded] = useState(true)

  const roles: UseQueryResult<Role[]> = useQuery(['roles'], () => fetchRoles(0, 100, 'name'))

  const role = roles.data?.find((r) => r.referenceId === roleId)

  if (roles.isLoading) {
    return <Typography>Loading...</Typography>
  }

  if (roles.error) {
    return (
      <Alert severity="error">
        <>
          <AlertTitle>Error fetching Role</AlertTitle>
          {roles.error}
        </>
      </Alert>
    )
  }

  if (!role) {
    return (
      <Alert severity="warning">
        <>
          <AlertTitle>Role not found</AlertTitle>
          The requested role could not be found.
        </>
      </Alert>
    )
  }

  const dataPermissionsByLevel = role.dataPermissions.reduce((acc, dp) => {
    if (!acc[dp.level]) {
      acc[dp.level] = []
    }
    acc[dp.level].push(dp)
    return acc
  }, {} as Record<string, typeof role.dataPermissions>)

  const appPermissionsByApp = role.applicationPermissions.reduce((acc, ap) => {
    if (!acc[ap.application]) {
      acc[ap.application] = []
    }
    acc[ap.application].push(ap)
    return acc
  }, {} as Record<string, typeof role.applicationPermissions>)

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/roles')} sx={{ mb: 2 }}>
        Back to Roles
      </Button>

      <Typography variant="h4" color="primary" gutterBottom>
        {role.name}
      </Typography>

      {/* Application Permissions Section */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">Application Permissions ({role.applicationPermissions.length})</Typography>
          <IconButton onClick={() => setAppPermExpanded(!appPermExpanded)}>
            {appPermExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Divider sx={{ mt: 1, mb: 2 }} />
        <Collapse in={appPermExpanded}>
          {role.applicationPermissions.length === 0 ? (
            <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              No application permissions assigned
            </Typography>
          ) : (
            Object.entries(appPermissionsByApp).map(([app, permissions]) => (
              <Box key={app} sx={{ mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  {app}
                </Typography>
                <Box>
                  {permissions.map((perm, idx) => (
                    <StyledChip
                      key={`${app}-${perm.view}-${idx}`}
                      sx={{ m: 0.5 }}
                      label={perm.view}
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              </Box>
            ))
          )}
        </Collapse>
      </Paper>

      {/* Data Permissions Section */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">Data Permissions ({role.dataPermissions.length})</Typography>
          <IconButton onClick={() => setDataPermExpanded(!dataPermExpanded)}>
            {dataPermExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Divider sx={{ mt: 1, mb: 2 }} />
        <Collapse in={dataPermExpanded}>
          {role.dataPermissions.length === 0 ? (
            <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No data permissions assigned</Typography>
          ) : (
            Object.entries(dataPermissionsByLevel).map(([level, permissions]) => (
              <Box key={level} sx={{ mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  {level}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  {permissions
                    .sort((pA, pB) => pA.permission.resource.localeCompare(pB.permission.resource))
                    .map((dp, idx) => (
                      <StyledChip
                        key={`${level}-${dp.permission.resource}-${dp.permission.action}-${idx}`}
                        sx={{ m: 0.5 }}
                        color="primary"
                        icon={actionToIcon[dp.permission.action]}
                        label={`${dp.permission.resource} ${dp.permission.action}`}
                        variant="outlined"
                      />
                    ))}
                </Box>
              </Box>
            ))
          )}
        </Collapse>
      </Paper>

      {/* Global Permissions Section */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">Global Permissions ({role.globalPermissions.length})</Typography>
          <IconButton onClick={() => setGlobalPermExpanded(!globalPermExpanded)}>
            {globalPermExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Divider sx={{ mt: 1, mb: 2 }} />
        <Collapse in={globalPermExpanded}>
          {role.globalPermissions.length === 0 ? (
            <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              No global permissions assigned
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {role.globalPermissions
                .sort((pA, pB) => pA.permission.resource.localeCompare(pB.permission.resource))
                .map((dp, idx) => (
                  <StyledChip
                    key={`global-${dp.permission.resource}-${dp.permission.action}-${idx}`}
                    sx={{ m: 0.5 }}
                    color="primary"
                    icon={actionToIcon[dp.permission.action]}
                    label={`${dp.permission.resource} ${dp.permission.action}`}
                    variant="outlined"
                  />
                ))}
            </Box>
          )}
        </Collapse>
      </Paper>
    </Box>
  )
}

export default RoleDetails
